import { useRouteQuery } from '@/common/route/useRouteQuery';
import { useBaseListView } from '@/common/view/useBaseListView';
import { useCrudActions } from '@/common/view/useCrudActions';
import { SelectOption } from '@/components/Select.vue';
import { ROUTE_NAME_GAME } from '@/modules/game/router';
import { useGameVariantList } from '@/modules/game/variant/model/list';
import { gameService } from '@/services/game';
import { computed, onMounted, ref } from 'vue';

export function useGameMatchList() {
    const crudAction = useCrudActions();

    const { query, updateQueryToRoute } = useRouteQuery({
        name: ROUTE_NAME_GAME.MATCH.INDEX,
        definition: {
            variantId: { type: 'number', default: null }
        },
        onChange: () => fetchList()
    });

    const { variantOptions, variantNameMap, fetchList: fetchVariantList } = useGameVariantList();

    const { list, total, fetchList } = useBaseListView({
        request: gameService.match.list,
        paramsBuilder: () => ({ ...query }),
        hooks: {
            onSuccess: updateQueryToRoute,
            canFetch: () => query.variantId != null
        }
    });

    const gameMatchCache = ref(new Map<number, SelectOption & { data: any }>());
    const gameMatchOptions = computed((): Array<SelectOption> => Array.from(gameMatchCache.value.values()));
    const fetchAll = crudAction.executeAction({
        request: () => gameService.match.list(),
        onSuccess: ({ data }) => {
            gameMatchCache.value.clear();

            for (const item of data) {
                const variantName = variantNameMap.value.get(item.variantId);
                if (variantName) {
                    gameMatchCache.value.set(item.id, {
                        value: item.id,
                        label: `${variantName}: ${item.name} - ${item.maxRound} ${
                            item.maxRound > 1 ? 'rounds' : 'round'
                        }`,
                        data: item
                    });
                }
            }
        }
    });

    const actions = {
        remove: crudAction.confirmAndRemove({
            request: gameService.match.remove,
            onSuccess: () => fetchList()
        })
    };

    onMounted(() => fetchVariantList());

    return {
        query,
        variantOptions,
        fetchList,
        list,
        total,
        fetchAllMatches: fetchAll,
        gameMatchCache,
        gameMatchOptions,
        actions
    };
}
